// WindTrails.tsx
import * as THREE from 'three';
import React, { useRef, useState, useEffect } from 'react';
import { Trail } from '@react-three/drei';
import { ThreeElements, useFrame } from '@react-three/fiber';
import { toVec3 } from '@slope/three';

const _center = /*#__PURE__*/ new THREE.Vector3();
const _halfSize = /*#__PURE__*/ new THREE.Vector3();

type WindTrailsProps = ThreeElements['group'] & {
  dirDegFrom: number; // meteorological bearing, 0°=North(+Z)
  speedMs: number; // meters/second
  center?: THREE.Vector3 | [number, number, number];
  halfSize?: THREE.Vector3 | [number, number, number];
  count?: number;
  color?: string;
  width?: number;
  length?: number;
  decay?: number;
  speedScale?: number; // m/s -> world units/s
  jitter?: number; // wobble magnitude (world units/s)
  y?: number; // slight lift over ground
};

type Anchor = {
  ref: React.RefObject<THREE.Object3D | null>;
  pos: THREE.Vector3;
  seed: number;
  key: number;
};

export const WindTrails: React.FC<WindTrailsProps> = ({
  dirDegFrom,
  speedMs,
  center = [0, 0, 0],
  halfSize = [300, 0, 300],
  count = 60,
  color = '#ffffff',
  width = 1.6,
  length = 80,
  decay = 0.82,
  speedScale = 2.0,
  jitter = 0.6,
  y = 0.15,
  ...restProps
}) => {
  const centerVec = toVec3(center, _center);
  const halfSizeVec = toVec3(halfSize, _halfSize);

  // FROM bearing → motion TO the opposite
  const wind = dirFromDeg(dirDegFrom).multiplyScalar(-1).normalize();

  // anchors + per-anchor “key” for selective remount
  const [anchors, setAnchors] = useState<Anchor[]>(() =>
    Array.from(
      { length: count },
      (_, i): Anchor => ({
        ref: React.createRef<THREE.Object3D>(), // generic is Object3D; current is Object3D | null
        pos: randomSpawnOnUpwindEdge(centerVec, halfSizeVec, wind, y),
        seed: Math.random() * 1000 + i * 13.37,
        key: 0,
      }),
    ),
  );

  // if inputs that change the field change → reset all trails
  useEffect(() => {
    setAnchors((arr) =>
      arr.map((a) => ({
        ...a,
        pos: randomSpawnOnUpwindEdge(centerVec, halfSizeVec, wind, y),
        key: a.key + 1,
      })),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [centerVec.x, centerVec.y, centerVec.z, halfSizeVec.x, halfSizeVec.z, dirDegFrom]);

  // cheap temp vectors
  const vel = useRef(new THREE.Vector3());
  const jv = useRef(new THREE.Vector3());

  useFrame((_, dt) => {
    const speed = speedMs * speedScale;
    vel.current.copy(wind).multiplyScalar(speed);

    let needRerender = false;

    setAnchors((arr) =>
      arr.map((a) => {
        // jitter
        const n = pseudoNoise(a.seed + performance.now() * 0.00035);
        jv.current.set(Math.sin(n * 6.283) * jitter, 0, Math.cos(n * 6.283) * jitter);

        // move
        a.pos.addScaledVector(vel.current, dt).addScaledVector(jv.current, dt);

        // if out of bounds → respawn on upwind edge and REMOUNT trail
        if (!insideRect(a.pos, centerVec, halfSizeVec)) {
          a.pos.copy(randomSpawnOnUpwindEdge(centerVec, halfSizeVec, wind, y));
          a.key += 1;
          needRerender = true;
        }

        if (a.ref.current) a.ref.current.position.copy(a.pos);
        return a;
      }),
    );

    // bump React so changed keys remount only the affected trails
    if (needRerender) setTick((t) => t + 1);
  });

  // force rerender state
  const [, setTick] = useState(0);

  return (
    <group {...restProps}>
      {anchors.map((a, i) => (
        <Trail
          key={`${i}-${a.key}`}
          width={width}
          length={length}
          decay={decay}
          color={color}
          attenuation={(t) => t * t}
        >
          <object3D ref={a.ref} position={a.pos.toArray()} />
        </Trail>
      ))}
    </group>
  );
};

/** 0°=North(+Z), 90°=East(+X) */
function dirFromDeg(deg: number) {
  const r = (deg * Math.PI) / 180;
  return new THREE.Vector3(Math.sin(r), 0, Math.cos(r));
}
function insideRect(p: THREE.Vector3, c: THREE.Vector3, h: THREE.Vector3) {
  return p.x >= c.x - h.x && p.x <= c.x + h.x && p.z >= c.z - h.z && p.z <= c.z + h.z;
}
/** spawn at the UPWIND boundary so all trails flow across the region */
function randomSpawnOnUpwindEdge(
  center: THREE.Vector3,
  halfSize: THREE.Vector3,
  windTo: THREE.Vector3,
  height = 0,
) {
  const p = new THREE.Vector3();

  // decide which boundary is upwind by comparing wind direction components
  if (Math.abs(windTo.x) > Math.abs(windTo.z)) {
    // moving mostly +X or -X → spawn at opposite X edge
    const sign = Math.sign(windTo.x); // +1 to +X, so upwind is -sign
    p.x = center.x - sign * halfSize.x;
    p.z = center.z + (Math.random() * 2 - 1) * halfSize.z;
  } else {
    const sign = Math.sign(windTo.z);
    p.z = center.z - sign * halfSize.z;
    p.x = center.x + (Math.random() * 2 - 1) * halfSize.x;
  }
  p.y = center.y + height;
  return p;
}
function pseudoNoise(x: number) {
  return (Math.sin(x * 12.9898) * 43758.5453) % 1;
}
